import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { TONE_CLASSES, type StatusTone } from '../status/registry'

export interface BannerProps {
  tone: StatusTone
  icon?: LucideIcon
  title: ReactNode
  description?: ReactNode
  /** Trailing slot (meta text, CTA button…), aligned to the right edge. */
  action?: ReactNode
  className?: string
}

/**
 * Full-width tone banner (soft tone background + optional icon + message).
 * i18n-agnostic: consumers supply already-translated content. Danger tone gets
 * `role="alert"` for assistive tech; other tones stay silent.
 */
export function Banner({ tone, icon: Icon, title, description, action, className }: BannerProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-3 rounded-lg px-4 py-3',
        TONE_CLASSES[tone].soft,
        className,
      )}
    >
      {Icon ? <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden /> : null}
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="font-semibold">{title}</span>
        {description ? <span className="break-words text-sm opacity-90">{description}</span> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
